import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Lightbulb, Send, Loader2, Trash2, Play, Check, Undo2, ListTodo, Rocket, CheckCircle2,
  ImagePlus, X,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const authToken = () => localStorage.getItem("phs_token") || "";
const imgUrl = (reqId, imgId) =>
  `${API_BASE}/api/feature-requests/${reqId}/images/${imgId}?token=${encodeURIComponent(authToken())}`;

const COLUMNS = [
  { key: "requested", label: "Requested", icon: ListTodo, dot: "bg-amber-500" },
  { key: "in_progress", label: "In Progress", icon: Rocket, dot: "bg-[hsl(var(--primary))]" },
  { key: "done", label: "Accomplished", icon: CheckCircle2, dot: "bg-[hsl(var(--success))]" },
];

const PRIORITY_CLS = {
  high: "border-red-500/40 bg-red-500/10 text-red-500",
  normal: "border-border bg-secondary/60 text-muted-foreground",
  low: "border-sky-500/40 bg-sky-500/10 text-sky-500",
};

function fmtDate(iso) {
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
}

function RequestCard({ r, isAdmin, canEdit, onMove, onDelete, onAddImage, onDeleteImage, onOpenImage }) {
  const [confirmDel, setConfirmDel] = useState(false);
  return (
    <div className="rounded-lg border border-border bg-card p-3 shadow-sm" data-testid={`request-card-${r.id}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-semibold leading-snug">{r.title}</p>
        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${PRIORITY_CLS[r.priority] || PRIORITY_CLS.normal}`}>
          {r.priority}
        </span>
      </div>
      {r.description && <p className="mt-1.5 whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground">{r.description}</p>}

      {r.images && r.images.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-2" data-testid={`request-images-${r.id}`}>
          {r.images.map((im) => (
            <div key={im.id} className="group relative">
              <img
                src={imgUrl(r.id, im.id)}
                alt={im.filename}
                loading="lazy"
                onClick={() => onOpenImage(imgUrl(r.id, im.id))}
                className="h-16 w-16 cursor-pointer rounded-md border border-border object-cover transition hover:opacity-90"
                data-testid={`request-image-${im.id}`}
              />
              {canEdit && (
                <button
                  onClick={() => onDeleteImage(r, im)}
                  className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white shadow"
                  data-testid={`request-image-delete-${im.id}`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
        <span className="truncate">{r.submitted_by}{r.submitted_email ? ` · ${r.submitted_email}` : ""}</span>
        <span className="shrink-0">{fmtDate(r.completed_at || r.created_at)}</span>
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        {r.status !== "requested" && (
          <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={() => onMove(r, r.status === "done" ? "in_progress" : "requested")} data-testid={`request-back-${r.id}`}>
            <Undo2 className="mr-1 h-3 w-3" /> Back
          </Button>
        )}
        {r.status === "requested" && (
          <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => onMove(r, "in_progress")} data-testid={`request-start-${r.id}`}>
            <Play className="mr-1 h-3 w-3" /> Start
          </Button>
        )}
        {r.status !== "done" && (
          <Button size="sm" className="h-7 px-2 text-[11px]" onClick={() => onMove(r, "done")} data-testid={`request-done-${r.id}`}>
            <Check className="mr-1 h-3 w-3" /> Done
          </Button>
        )}
        {canEdit && (
          <label className="inline-flex h-7 cursor-pointer items-center rounded-md px-2 text-[11px] font-medium text-muted-foreground hover:bg-secondary" data-testid={`request-add-image-${r.id}`}>
            <ImagePlus className="mr-1 h-3 w-3" /> Image
            <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { onAddImage(r, e.target.files); e.target.value = ""; }} />
          </label>
        )}
        {isAdmin && (confirmDel ? (
          <div className="ml-auto flex items-center gap-1.5" data-testid={`request-delete-confirm-row-${r.id}`}>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px] text-red-500 hover:text-red-500" onClick={() => onDelete(r)} data-testid={`request-delete-confirm-${r.id}`}>
              <Trash2 className="mr-1 h-3 w-3" /> Delete?
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={() => setConfirmDel(false)} data-testid={`request-delete-cancel-${r.id}`}>
              Cancel
            </Button>
          </div>
        ) : (
          <Button size="sm" variant="ghost" className="ml-auto h-7 w-7 p-0 text-muted-foreground hover:text-red-500" onClick={() => setConfirmDel(true)} data-testid={`request-delete-${r.id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        ))}
      </div>
    </div>
  );
}

export default function Requests() {
  const { user, isAdmin } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [priority, setPriority] = useState("normal");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/feature-requests")
      .then((r) => setRequests(r.data.requests || []))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const grouped = useMemo(() => {
    const g = { requested: [], in_progress: [], done: [] };
    requests.forEach((r) => { (g[r.status] || g.requested).push(r); });
    return g;
  }, [requests]);

  const canEditRequest = (r) =>
    isAdmin || (user?.email && (r.submitted_email || "").toLowerCase() === user.email.toLowerCase());

  const uploadImages = async (reqId, fileList) => {
    const arr = Array.from(fileList || []);
    await Promise.all(arr.map((f) => {
      const fd = new FormData();
      fd.append("file", f);
      return api.post(`/feature-requests/${reqId}/images`, fd);
    }));
  };

  const submit = async () => {
    if (!title.trim()) return toast.error("Give your request a short title.");
    setSubmitting(true);
    try {
      const { data } = await api.post("/feature-requests", { title: title.trim(), description: desc.trim(), priority });
      if (files.length) await uploadImages(data.id, files);
      setTitle(""); setDesc(""); setPriority("normal"); setFiles([]);
      toast.success("Request sent to dev 🚀");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const addImage = async (r, fileList) => {
    if (!fileList || !fileList.length) return;
    try {
      await uploadImages(r.id, fileList);
      toast.success("Image added");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteImage = async (r, im) => {
    try {
      await api.delete(`/feature-requests/${r.id}/images/${im.id}`);
      setRequests((rs) => rs.map((x) => (x.id === r.id ? { ...x, images: (x.images || []).filter((y) => y.id !== im.id) } : x)));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const move = async (r, status) => {
    setRequests((rs) => rs.map((x) => (x.id === r.id ? { ...x, status } : x)));
    try { await api.put(`/feature-requests/${r.id}`, { status }); load(); }
    catch (e) { toast.error(apiError(e)); load(); }
  };

  const remove = async (r) => {
    setRequests((rs) => rs.filter((x) => x.id !== r.id));
    try { await api.delete(`/feature-requests/${r.id}`); }
    catch (e) { toast.error(apiError(e)); load(); }
  };

  return (
    <div className="animate-fade-up space-y-5" data-testid="requests-page">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-2xl font-black tracking-tighter">
          <Lightbulb className="h-6 w-6 text-[hsl(var(--primary))]" /> Dev Requests
        </h1>
        <p className="text-sm text-muted-foreground">Send feature ideas straight to the dev team and track what&apos;s shipped.</p>
      </div>

      {/* Highlighted submit area */}
      <div className="rounded-xl border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 p-4 shadow-sm" data-testid="request-submit-card">
        <p className="mb-2.5 flex items-center gap-1.5 text-sm font-bold text-[hsl(var(--primary))]">
          <Send className="h-4 w-4" /> Submit a request to Dev
        </p>
        <div className="grid gap-2.5 md:grid-cols-[1fr_auto]">
          <div className="space-y-2.5">
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What would you like us to build or change?"
              className="h-10 bg-card"
              data-testid="request-title-input"
            />
            <textarea
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="Add any detail — where in the app, why it helps, an example…"
              rows={2}
              className="w-full resize-y rounded-md border border-border bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/40"
              data-testid="request-desc-input"
            />
            <div className="flex flex-wrap items-center gap-2">
              <label className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-md border border-border bg-card px-3 text-sm font-semibold text-muted-foreground hover:bg-secondary" data-testid="request-attach-btn">
                <ImagePlus className="h-4 w-4" /> Attach images
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => { setFiles((f) => [...f, ...Array.from(e.target.files || [])]); e.target.value = ""; }}
                />
              </label>
              {files.map((f, i) => (
                <div key={i} className="relative">
                  <img src={URL.createObjectURL(f)} alt={f.name} className="h-14 w-14 rounded-md border border-border object-cover" />
                  <button
                    onClick={() => setFiles((arr) => arr.filter((_, j) => j !== i))}
                    className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white shadow"
                    data-testid={`request-attach-remove-${i}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-row items-start gap-2 md:flex-col">
            <div className="flex overflow-hidden rounded-md border border-border bg-card" data-testid="request-priority">
              {["low", "normal", "high"].map((p) => (
                <button
                  key={p}
                  onClick={() => setPriority(p)}
                  data-testid={`request-priority-${p}`}
                  className={`px-2.5 py-2 text-xs font-semibold capitalize transition ${priority === p ? "bg-[hsl(var(--primary))] text-white" : "text-muted-foreground hover:bg-secondary"}`}
                >
                  {p}
                </button>
              ))}
            </div>
            <Button onClick={submit} disabled={submitting} className="h-10 md:w-full" data-testid="request-submit-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-1.5 h-4 w-4" /> Send</>}
            </Button>
          </div>
        </div>
      </div>

      {/* Board */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" /></div>
      ) : (
        <div className="grid gap-4 md:grid-cols-3">
          {COLUMNS.map((col) => (
            <div key={col.key} className="rounded-xl border border-border bg-secondary/20 p-3" data-testid={`request-col-${col.key}`}>
              <div className="mb-3 flex items-center gap-2">
                <span className={`h-2.5 w-2.5 rounded-full ${col.dot}`} />
                <col.icon className="h-4 w-4 text-muted-foreground" />
                <span className="font-heading text-sm font-bold">{col.label}</span>
                <span className="ml-auto rounded-full bg-card px-2 py-0.5 text-xs font-bold text-muted-foreground" data-testid={`request-count-${col.key}`}>
                  {grouped[col.key].length}
                </span>
              </div>
              <div className="space-y-2.5">
                {grouped[col.key].length === 0 ? (
                  <p className="py-6 text-center text-xs text-muted-foreground">Nothing here yet.</p>
                ) : (
                  grouped[col.key].map((r) => (
                    <RequestCard
                      key={r.id}
                      r={r}
                      isAdmin={isAdmin}
                      canEdit={canEditRequest(r)}
                      onMove={move}
                      onDelete={remove}
                      onAddImage={addImage}
                      onDeleteImage={deleteImage}
                      onOpenImage={setLightbox}
                    />
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6"
          onClick={() => setLightbox(null)}
          data-testid="request-image-lightbox"
        >
          <img src={lightbox} alt="attachment" className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain shadow-2xl" />
        </div>
      )}
    </div>
  );
}
